window.theme = window.theme || {};

theme.config = {
  mqlSmall: false,
  mediaQuerySmall: "screen and (max-width: 749px)",
  isTouch:
    "ontouchstart" in window ||
    (window.DocumentTouch && window.document instanceof DocumentTouch) ||
    window.navigator.maxTouchPoints ||
    window.navigator.msMaxTouchPoints
      ? true
      : false,
  rtl: document.documentElement.getAttribute("dir") === "rtl" ? true : false,
};

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

class SectionId {
  static #separator = "__";

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(
    oldNode,
    newContent,
    preProcessCallbacks = [],
    postProcessCallbacks = []
  ) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement("div");
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll("[id], [form]").forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form &&
        element.setAttribute(
          "form",
          `${element.form.getAttribute("id")}-${uniqueKey}`
        );
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = "none";

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll("script").forEach((oldScriptTag) => {
      const newScriptTag = document.createElement("script");
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute("role", "button");
  summary.setAttribute(
    "aria-expanded",
    summary.parentNode.hasAttribute("open")
  );

  if (summary.nextElementSibling.getAttribute("id")) {
    summary.setAttribute("aria-controls", summary.nextElementSibling.id);
  }

  summary.addEventListener("click", (event) => {
    event.currentTarget.setAttribute(
      "aria-expanded",
      !event.currentTarget.closest("details").hasAttribute("open")
    );
  });

  if (summary.closest("header-drawer, menu-drawer")) return;
  summary.parentElement.addEventListener("keyup", onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (
      event.target !== container &&
      event.target !== last &&
      event.target !== first
    )
      return;

    document.addEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener("keydown", trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== "TAB") return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if (
      (event.target === container || event.target === first) &&
      event.shiftKey
    ) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener("focusout", trapFocusHandlers.focusout);
  document.addEventListener("focusin", trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === "INPUT" &&
    ["search", "text", "email", "url"].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(":focus-visible");
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    "ARROWUP",
    "ARROWDOWN",
    "ARROWLEFT",
    "ARROWRIGHT",
    "TAB",
    "ENTER",
    "SPACE",
    "ESCAPE",
    "HOME",
    "END",
    "PAGEUP",
    "PAGEDOWN",
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener("keydown", (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener("mousedown", (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    "focus",
    () => {
      if (currentFocusedElement)
        currentFocusedElement.classList.remove("focused");

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add("focused");
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll(".js-youtube").forEach((video) => {
    video.contentWindow.postMessage(
      '{"event":"command","func":"' + "pauseVideo" + '","args":""}',
      "*"
    );
  });
  document.querySelectorAll(".js-vimeo").forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', "*");
  });
  document.querySelectorAll("video").forEach((video) => video.pause());
  document.querySelectorAll("product-model").forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener("focusin", trapFocusHandlers.focusin);
  document.removeEventListener("focusout", trapFocusHandlers.focusout);
  document.removeEventListener("keydown", trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== "ESCAPE") return;

  const openDetailsElement = event.target.closest("details[open]");
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector("summary");
  openDetailsElement.removeAttribute("open");
  summaryElement.setAttribute("aria-expanded", false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector("input");
    this.changeEvent = new Event("change", { bubbles: true });
    this.input.addEventListener("change", this.onInputChange.bind(this));
    this.querySelectorAll("button").forEach((button) =>
      button.addEventListener("click", this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(
      PUB_SUB_EVENTS.quantityUpdate,
      this.validateQtyRules.bind(this)
    );
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === "plus") {
      if (
        parseInt(this.input.dataset.min) > parseInt(this.input.step) &&
        this.input.value == 0
      ) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value)
      this.input.dispatchEvent(this.changeEvent);

    if (
      this.input.dataset.min === previousValue &&
      event.target.name === "minus"
    ) {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle(
        "disabled",
        parseInt(value) <= parseInt(this.input.min)
      );
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle("disabled", value >= max);
    }
  }
}

customElements.define("quantity-input", QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = "json") {
  return {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Accept: `application/${type}`,
    },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == "undefined") {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent("on" + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options["method"] || "post";
  var params = options["parameters"] || {};

  var form = document.createElement("form");
  form.setAttribute("method", method);
  form.setAttribute("action", path);

  for (var key in params) {
    var hiddenField = document.createElement("input");
    hiddenField.setAttribute("type", "hidden");
    hiddenField.setAttribute("name", key);
    hiddenField.setAttribute("value", params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (
  country_domid,
  province_domid,
  options
) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(
    options["hideElement"] || province_domid
  );

  Shopify.addListener(
    this.countryEl,
    "change",
    Shopify.bind(this.countryHandler, this)
  );

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute("data-default");
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute("data-default");
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute("data-provinces");
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = "none";
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement("option");
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = "";
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement("option");
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector("details");

    this.addEventListener("keyup", this.onKeyUp.bind(this));
    this.addEventListener("focusout", this.onFocusOut.bind(this));
    this.bindEvents();
    this.bindFilterClose();
    this.suppressFilterScrollToTop();
  }

  suppressFilterScrollToTop() {
    // Another script (quick-add-modal) scrolls the window to the top whenever a
    // facet checkbox changes. We can't edit that script, so we patch
    // window.scrollTo once and ignore any "scroll to top" that fires shortly
    // after a filter checkbox change — leaving all other scrolling untouched.
    if (window.__menuDrawerScrollPatched) return;

    let suppressUntil = 0;
    const originalScrollTo = window.scrollTo.bind(window);

    window.scrollTo = function (...args) {
      const top =
        typeof args[0] === "object" && args[0] !== null ? args[0].top : args[1];
      if (Number(top) === 0 && Date.now() < suppressUntil) {
        return; // swallow the auto scroll-to-top during a filter change
      }
      return originalScrollTo(...args);
    };

    document.addEventListener(
      "change",
      (event) => {
        const target = event.target;
        if (
          target.matches &&
          target.matches('input[type="checkbox"]') &&
          target.closest(".facets-layout")
        ) {
          // The other script scrolls ~500ms later; keep the window a bit wider.
          suppressUntil = Date.now() + 1000;
        }
      },
      true
    );

    window.__menuDrawerScrollPatched = true;
  }

  bindFilterClose() {
    // The drawer markup is NOT re-rendered when a filter applies, so nothing
    // closes it on its own. Shopify updates the URL via history.pushState on
    // every filter change (whether live-apply or via an Apply button), so we
    // patch pushState once and reset the drawer whenever the URL changes.
    if (!window.__menuDrawerLocationPatched) {
      const originalPushState = history.pushState;
      history.pushState = function () {
        const result = originalPushState.apply(this, arguments);
        window.dispatchEvent(new Event("menu-drawer:locationchange"));
        return result;
      };
      window.addEventListener("popstate", () =>
        window.dispatchEvent(new Event("menu-drawer:locationchange"))
      );
      window.__menuDrawerLocationPatched = true;
    }

    window.addEventListener("menu-drawer:locationchange", () =>
      this.resetDrawer()
    );
  }

  // Synchronous, complete teardown of the drawer state. Unlike closeMenuDrawer
  // / closeSubmenu, this removes `open`, `menu-opening`, `submenu-open`, the
  // body scroll lock and trap focus all at once, with no 400ms animation delay.
  resetDrawer() {
    if (!this.mainDetailsToggle) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.removeAttribute("open");

    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => submenu.classList.remove("submenu-open"));

    const summary = this.mainDetailsToggle.querySelector("summary");
    if (summary) summary.setAttribute("aria-expanded", false);

    this.removeBodyLock();
    if (typeof removeTrapFocus === "function") removeTrapFocus();
  }

  connectedCallback() {
    // If a previous instance left the body locked (e.g. after a filter
    // re-render replaced the old drawer markup), clear it on connect.
    this.removeBodyLock();
  }

  disconnectedCallback() {
    // This node is being replaced by a section re-render — release the
    // scroll lock so the page stays scrollable.
    this.removeBodyLock();
  }

  removeBodyLock() {
    // Remove the lock for this breakpoint AND any leftover overflow-hidden-*
    // class, so scroll is restored regardless of the data-breakpoint value.
    if (this.dataset.breakpoint) {
      document.body.classList.remove(
        `overflow-hidden-${this.dataset.breakpoint}`
      );
    }
    [...document.body.classList]
      .filter((c) => c.startsWith("overflow-hidden-"))
      .forEach((c) => document.body.classList.remove(c));
  }

  bindEvents() {
    this.querySelectorAll("summary").forEach((summary) =>
      summary.addEventListener("click", this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      "button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)"
    ).forEach((button) =>
      button.addEventListener("click", this.onCloseButtonClick.bind(this))
    );
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== "ESCAPE") return;

    const openDetailsElement = event.target.closest("details[open]");
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(
          event,
          this.mainDetailsToggle.querySelector("summary")
        )
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest(".has-submenu");
    const isOpen = detailsElement.hasAttribute("open");
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");

    function addTrapFocus() {
      trapFocus(
        summaryElement.nextElementSibling,
        detailsElement.querySelector("button")
      );
      summaryElement.nextElementSibling.removeEventListener(
        "transitionend",
        addTrapFocus
      );
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen
        ? this.closeMenuDrawer(event, summaryElement)
        : this.openMenuDrawer(summaryElement);

      if (window.matchMedia("(max-width: 990px)").matches) {
        document.documentElement.style.setProperty(
          "--viewport-height",
          `${window.innerHeight}px`
        );
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add("menu-opening");
        summaryElement.setAttribute("aria-expanded", true);
        parentMenuElement && parentMenuElement.classList.add("submenu-open");
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener(
              "transitionend",
              addTrapFocus
            );
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });
    summaryElement.setAttribute("aria-expanded", true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove("menu-opening");
    this.mainDetailsToggle.querySelectorAll("details").forEach((details) => {
      details.removeAttribute("open");
      details.classList.remove("menu-opening");
    });
    this.mainDetailsToggle
      .querySelectorAll(".submenu-open")
      .forEach((submenu) => {
        submenu.classList.remove("submenu-open");
      });
    document.body.classList.remove(
      `overflow-hidden-${this.dataset.breakpoint}`
    );
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    if (event instanceof KeyboardEvent)
      elementToFocus?.setAttribute("aria-expanded", false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (
        this.mainDetailsToggle.hasAttribute("open") &&
        !this.mainDetailsToggle.contains(document.activeElement)
      )
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest("details");
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest(".submenu-open");
    parentMenuElement && parentMenuElement.classList.remove("submenu-open");
    detailsElement.classList.remove("menu-opening");
    detailsElement
      .querySelector("summary")
      .setAttribute("aria-expanded", false);
    removeTrapFocus(detailsElement.querySelector("summary"));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute("open");
        if (detailsElement.closest("details[open]")) {
          trapFocus(
            detailsElement.closest("details[open]"),
            detailsElement.querySelector("summary")
          );
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define("menu-drawer", MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector(".section-header");
    this.borderOffset =
      this.borderOffset ||
      this.closest(".header-wrapper").classList.contains(
        "header-wrapper--border-bottom"
      )
        ? 1
        : 0;
    document.documentElement.style.setProperty(
      "--header-bottom-position",
      `${parseInt(
        this.header.getBoundingClientRect().bottom - this.borderOffset
      )}px`
    );
    this.header.classList.add("menu-open");

    setTimeout(() => {
      this.mainDetailsToggle.classList.add("menu-opening");
    });

    summaryElement.setAttribute("aria-expanded", true);
    window.addEventListener("resize", this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove("menu-open");
    window.removeEventListener("resize", this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        "--header-bottom-position",
        `${parseInt(
          this.header.getBoundingClientRect().bottom - this.borderOffset
        )}px`
      );
    document.documentElement.style.setProperty(
      "--viewport-height",
      `${window.innerHeight}px`
    );
  };
}

customElements.define("header-drawer", HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]').addEventListener(
      "click",
      this.hide.bind(this, false)
    );
    this.addEventListener("keyup", (event) => {
      if (event.code.toUpperCase() === "ESCAPE") this.hide();
    });
    if (this.classList.contains("media-modal")) {
      this.addEventListener("pointerup", (event) => {
        if (
          event.pointerType === "mouse" &&
          !event.target.closest("deferred-media, product-model")
        )
          this.hide();
      });
    } else {
      this.addEventListener("click", (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    this.dataset.section = this.closest(".shopify-section").id.replace(
      "shopify-section-",
      ""
    );
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector(".template-popup");
    document.body.classList.add("overflow-hidden");
    this.setAttribute("open", "");
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove("overflow-hidden");
    document.body.dispatchEvent(new CustomEvent("modalClosed"));
    this.removeAttribute("open");
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define("modal-dialog", ModalDialog);

class BulkModal extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === "") {
        const productUrl = this.dataset.url.split("?")[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(
              responseText,
              "text/html"
            );
            const sourceQty = html.querySelector(
              ".quick-order-list-container"
            ).parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };

    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(
        `#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`
      )
    );
  }
}

customElements.define("bulk-modal", BulkModal);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector("button");

    if (!button) return;
    button.addEventListener("click", () => {
      const modal = document.querySelector(this.getAttribute("data-modal"));
      if (modal) modal.show(button);
    });
  }
}
customElements.define("modal-opener", ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener("click", this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute("loaded")) {
      const content = document.createElement("div");
      content.appendChild(
        this.querySelector("template").content.firstElementChild.cloneNode(true)
      );

      this.setAttribute("loaded", true);
      const deferredElement = this.appendChild(
        content.querySelector("video, model-viewer, iframe")
      );
      if (focus) deferredElement.focus();
      if (
        deferredElement.nodeName == "VIDEO" &&
        deferredElement.getAttribute("autoplay")
      ) {
        // force autoplay for safari
        deferredElement.play();
      }

      // Workaround for safari iframe bug
      const formerStyle = deferredElement.getAttribute("style");
      deferredElement.setAttribute("style", "display: block;");
      window.setTimeout(() => {
        deferredElement.setAttribute("style", formerStyle);
      }, 0);
    }
  }
}

customElements.define("deferred-media", DeferredMedia);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector(".slider-counter--current");
    this.pageTotalElement = this.querySelector(".slider-counter--total");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener("scroll", this.update.bind(this));
    this.prevButton.addEventListener("click", this.onButtonClick.bind(this));
    this.nextButton.addEventListener("click", this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(
      (element) => element.clientWidth > 0
    );
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset =
      this.sliderItemsToShow[1].offsetLeft -
      this.sliderItemsToShow[0].offsetLeft;
    this.slidesPerPage = Math.floor(
      (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) /
        this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage =
      Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      this.dispatchEvent(
        new CustomEvent("slideChanged", {
          detail: {
            currentPage: this.currentPage,
            currentElement: this.sliderItemsToShow[this.currentPage - 1],
          },
        })
      );
    }

    if (this.enableSliderLooping) return;

    if (
      this.isSlideVisible(this.sliderItemsToShow[0]) &&
      this.slider.scrollLeft === 0
    ) {
      this.prevButton.setAttribute("disabled", "disabled");
    } else {
      this.prevButton.removeAttribute("disabled");
    }

    if (
      this.isSlideVisible(
        this.sliderItemsToShow[this.sliderItemsToShow.length - 1]
      )
    ) {
      this.nextButton.setAttribute("disabled", "disabled");
    } else {
      this.nextButton.removeAttribute("disabled");
    }
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide =
      this.slider.clientWidth + this.slider.scrollLeft - offset;
    return (
      element.offsetLeft + element.clientWidth <= lastVisibleSlide &&
      element.offsetLeft >= this.slider.scrollLeft
    );
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === "next"
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define("slider-component", SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector(".slider-buttons");
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector(".slideshow__slide");
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector(".announcement-bar-slider");
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(
      this.sliderControlWrapper.querySelectorAll(".slider-counter__link")
    );
    this.sliderControlLinksArray.forEach((link) =>
      link.addEventListener("click", this.linkToSlide.bind(this))
    );
    this.slider.addEventListener("scroll", this.setSlideVisibility.bind(this));
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia(
        "(prefers-reduced-motion: reduce)"
      );
      this.reducedMotion.addEventListener("change", () => {
        if (this.slider.getAttribute("data-autoplay") === "true")
          this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          "click",
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute("data-autoplay") === "true")
      this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener("mouseover", this.focusInHandling.bind(this));
    this.addEventListener("mouseleave", this.focusOutHandling.bind(this));
    this.addEventListener("focusin", this.focusInHandling.bind(this));
    this.addEventListener("focusout", this.focusOutHandling.bind(this));

    if (this.querySelector(".slideshow__autoplay")) {
      this.sliderAutoplayButton = this.querySelector(".slideshow__autoplay");
      this.sliderAutoplayButton.addEventListener(
        "click",
        this.autoPlayToggle.bind(this)
      );
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked
        ? this.pause()
        : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === "previous") {
      this.slideScrollPosition =
        this.slider.scrollLeft +
        this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === "next") {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll(".slider-counter__link");
    this.prevButton.removeAttribute("disabled");

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove("slider-counter__link--active");
      link.removeAttribute("aria-current");
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add(
      "slider-counter__link--active"
    );
    this.sliderControlButtons[this.currentPage - 1].setAttribute(
      "aria-current",
      true
    );
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (
      !this.reducedMotion.matches &&
      !this.announcementBarArrowButtonWasClicked
    ) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton ||
        this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute("aria-live", "off");
    clearInterval(this.autoplay);
    this.autoplay = setInterval(
      this.autoRotateSlides.bind(this),
      this.autoplaySpeed
    );
  }

  pause() {
    this.slider.setAttribute("aria-live", "polite");
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.playSlideshow
      );
    } else {
      this.sliderAutoplayButton.classList.remove("slideshow__autoplay--paused");
      this.sliderAutoplayButton.setAttribute(
        "aria-label",
        window.accessibilityStrings.pauseSlideshow
      );
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll("a");
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute("tabindex");
          });
        item.setAttribute("aria-hidden", "false");
        item.removeAttribute("tabindex");
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute("tabindex", "-1");
          });
        item.setAttribute("aria-hidden", "true");
        item.setAttribute("tabindex", "-1");
      }
    });
    this.wasClicked = false;
  }

  applyAnimationToAnnouncementBar(button = "next") {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === "next" ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = "announcement-bar-slider--fade-in";
    const animationClassOut = "announcement-bar-slider--fade-out";

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext =
      (button === "next" && !isLastSlide) ||
      (button === "previous" && isFirstSlide);
    const direction = shouldMoveNext ? "next" : "previous";

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) +
          1 -
          this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define("slideshow-component", SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("change", (event) => {
      const target = this.getInputForEventTarget(event.target);
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === "SELECT" && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute("selected"))
        .removeAttribute("selected");
      target.selectedOptions[0].setAttribute("selected", "selected");

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest(".product-form__input")
        .querySelector("[data-selected-value] > .swatch");
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          swatchValue
        );
        selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
      } else {
        selectedDropdownSwatchValue.style.setProperty(
          "--swatch--background",
          "unset"
        );
        selectedDropdownSwatchValue.classList.add("swatch--unavailable");
      }

      selectedDropdownSwatchValue.style.setProperty(
        "--swatch-focal-point",
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset"
      );
    } else if (tagName === "INPUT" && target.type === "radio") {
      const selectedSwatchValue = target
        .closest(`.product-form__input`)
        .querySelector("[data-selected-value]");
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
  }

  get selectedOptionValues() {
    return Array.from(
      this.querySelectorAll("select option[selected], fieldset input:checked")
    ).map(({ dataset }) => dataset.optionValueId);
  }
}

customElements.define("variant-selects", VariantSelects);

class ProductRecommendations extends HTMLElement {
  observer = undefined;

  constructor() {
    super();
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: "0px 0px 400px 0px" }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(
      `${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`
    )
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("product-recommendations");

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (
          !this.querySelector("slideshow-component") &&
          this.classList.contains("complementary-products")
        ) {
          this.remove();
        }

        if (html.querySelector(".grid__item")) {
          this.classList.add("product-recommendations--loaded");
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define("product-recommendations", ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector(".icon");
  }

  connectedCallback() {
    document.addEventListener(
      "storefront:signincompleted",
      this.handleStorefrontSignInCompleted.bind(this)
    );
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define("account-icon", AccountIcon);

class BulkAdd extends HTMLElement {
  static ASYNC_REQUEST_DELAY = 250;

  constructor() {
    super();
    this.queue = [];
    this.setRequestStarted(false);
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });

    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, BulkAdd.ASYNC_REQUEST_DELAY);
  }

  sendRequest(queue) {
    this.setRequestStarted(true);
    const items = {};

    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter(
      (queueElement) => !queue.includes(queueElement)
    );

    this.updateMultipleQty(items);
  }

  setRequestStarted(requestStarted) {
    this._requestStarted = requestStarted;
  }

  get requestStarted() {
    return this._requestStarted;
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute("value");
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.min_error.replace(
          "[min]",
          event.target.dataset.min
        )
      );
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.max_error.replace(
          "[max]",
          event.target.max
        )
      );
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(
        event,
        index,
        window.quickOrderListStrings.step_error.replace(
          "[step]",
          event.target.step
        )
      );
    } else {
      event.target.setCustomValidity("");
      event.target.reportValidity();
      event.target.setAttribute("value", inputValue);
      this.startQueue(index, inputValue);
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }
}

if (!customElements.get("bulk-add")) {
  customElements.define("bulk-add", BulkAdd);
}

class CartPerformance {
  static #metric_prefix = "cart-performance";

  static createStartingMarker(benchmarkName) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    return performance.mark(`${metricName}:start`);
  }

  static measureFromEvent(benchmarkName, event) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`, {
      startTime: event.timeStamp,
    });

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }

  static measureFromMarker(benchmarkName, startMarker) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(benchmarkName, startMarker.name, `${metricName}:end`);
  }

  static measure(benchmarkName, callback) {
    const metricName = `${CartPerformance.#metric_prefix}:${benchmarkName}`;
    const startMarker = performance.mark(`${metricName}:start`);

    callback();

    const endMarker = performance.mark(`${metricName}:end`);

    performance.measure(
      benchmarkName,
      `${metricName}:start`,
      `${metricName}:end`
    );
  }
}

theme.initWhenVisible = function (options) {
  const threshold = options.threshold ? options.threshold : 0;

  const observer = new IntersectionObserver(
    (entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          if (typeof options.callback === "function") {
            options.callback();
            observer.unobserve(entry.target);
          }
        }
      });
    },
    { rootMargin: `0px 0px ${threshold}px 0px` }
  );

  observer.observe(options.element);
};

function isStorageSupported(type) {
  // Return false if we are in an iframe without access to sessionStorage
  if (window.self !== window.top) {
    return false;
  }

  const testKey = "beyours:test";
  let storage;
  if (type === "session") {
    storage = window.sessionStorage;
  }
  if (type === "local") {
    storage = window.localStorage;
  }

  try {
    storage.setItem(testKey, "1");
    storage.removeItem(testKey);
    return true;
  } catch (error) {
    // Do nothing, this may happen in Safari in incognito mode
    return false;
  }
}

class ProductRecentlyViewed extends HTMLElement {
  constructor() {
    super();

    // We save the product ID in local storage to be eventually used for recently viewed section
    if (isStorageSupported("local")) {
      const productId = parseInt(this.dataset.productId);
      const cookieName = "beyours:recently-viewed";
      const items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");

      // We check if the current product already exists, and if it does not, we add it at the start
      if (!items.includes(productId)) {
        items.unshift(productId);
      }

      // Keep 6: the current product is excluded on display, leaving 5 to fill the row
      window.localStorage.setItem(
        cookieName,
        JSON.stringify(items.slice(0, 6))
      );
    }
  }
}
customElements.define("product-recently-viewed", ProductRecentlyViewed);

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();

    theme.initWhenVisible({
      element: this,
      callback: this.init.bind(this),
      threshold: 600,
    });
  }

  init() {
    //console.log(this.dataset.url, this.getQueryString());
    fetch(this.dataset.url + this.getQueryString())
      .then((response) => response.text())
      .then((text) => {
        // console.log("text", text);
        const html = document.createElement("div");
        html.innerHTML = text;
        const recommendations = html.querySelector("recently-viewed-products");
        if (recommendations && recommendations.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }

  getQueryString() {
    const cookieName = "beyours:recently-viewed";
    const items = JSON.parse(window.localStorage.getItem(cookieName) || "[]");
    if (
      this.dataset.productId &&
      items.includes(parseInt(this.dataset.productId))
    ) {
      items.splice(items.indexOf(parseInt(this.dataset.productId)), 1);
    }
    return items
      .map((item) => "id:" + item)
      .slice(0, 5)
      .join(" OR ");
  }
}
customElements.define("recently-viewed-products", RecentlyViewedProducts);

document.addEventListener("DOMContentLoaded", function () {
  const playIcons = document.querySelectorAll(
    ".z_play_icon img, .z_play_icon svg"
  );
  const pauseIcons = document.querySelectorAll(
    "#z_video_pause img, #z_video_pause svg"
  );
  const video = document.getElementById("video1");
  const playButton = document.getElementById("z_video_play");
  const pauseButton = document.getElementById("z_video_pause");

  playIcons.forEach((icon) => {
    icon.addEventListener("click", function () {
      if (video) {
        video.play();
        playButton.style.display = "none";
        pauseButton.style.display = "block";
      }
    });
  });

  pauseIcons.forEach((icon) => {
    icon.addEventListener("click", function () {
      if (video) {
        video.pause();
        pauseButton.style.display = "none";
        playButton.style.display = "block";
      }
    });
  });
});

// NOTE: This file only contains the changed portions relevant to the
// custom bucket / gallery fix. Paste these replacements into your existing
// theme.js in place of the matching sections.

// ===========================================================================
//  AddToBucket — fixed
// ===========================================================================
//  Changes vs. original:
//  1. updateImageContainers no longer wipes .gallery-row (which was
//     destroying .gallery-thumbs-grid and .image-stack-container).
//     It now populates the correct child containers: .label-front-img,
//     .gallery-thumbs-grid, and .image-stack-container, using the same
//     fields the checkout handler uses (labelpic / nutrifact / ingredient).
//  2. Method declarations moved inside the class body consistently.
//  3. getEmptyCard simplified.
// ===========================================================================


// Serializes "Add to Bucket" clicks across every button on the page. Each
// click does an async inventory check, then only afterwards writes to
// localStorage — without this, rapid/repeat clicks all read the same
// pre-write localStorage state and each independently see "nothing
// committed yet", letting more units through than are actually in stock.
// Chaining every click onto this one promise forces them to run one at a
// time, so click #2's check only starts once click #1 has fully finished.
let addToBucketMutex = Promise.resolve();

class AddToBucket extends HTMLElement {
  constructor() {
    super();
    this.bucket = this;
  }

//  connectedCallback() {
//   this.addEventListener("click", async () => {
//     const url = this.bucket.dataset.url + "/products.js";
//     const response = await fetch(url);
//     const data = await response.json();

//     // ---- STOCK CHECK ----------------------------------------------------
//     const variant = data.variants[0]; // the "pouch" variant
//     const variantId = variant.id;

//     // Find this product's inventory data, rendered by Liquid into a
//     // <script class="variant-stock-data" data-product-id="..."> block.
//     const productId = data.id;
//     const stockScript = document.querySelector(
//       `script.variant-stock-data[data-product-id="${productId}"]`
//     );

//     let stock = Infinity; // fallback if we can't find the script
//     if (stockScript) {
//       try {
//         const stockMap = JSON.parse(stockScript.textContent);
//         if (stockMap[variantId] !== undefined && stockMap[variantId] !== null) {
//           stock = stockMap[variantId];
//         }
//       } catch (e) {
//         console.warn("[AddToBucket] Couldn't parse variant-stock-data:", e);
//       }
//     }

//     // How many of THIS variant are already in the bucket?
//     const existingCards = JSON.parse(localStorage.getItem("cards")) || [];
//     const alreadyInBucket = existingCards.filter(c => c.id === variantId).length;

//     if (alreadyInBucket + 1 > stock) {
//       if (typeof alertify !== "undefined") {
//         alertify.error(
//           stock <= 0
//             ? "This product is out of stock."
//             : `Only ${stock} available — you already have ${alreadyInBucket} in your bucket.`
//         );
//       }
//       return; // bail before touching the DOM or localStorage
//     }
//     // ---------------------------------------------------------------------

//     const image = data.featured_image;
//     const index = this.bucket.getEmptyCard();
//     if (index < 0) return; // no empty slot

//     const nutrifacts_image = this.bucket.dataset.nutrifact;
//     const ingredients_image = this.bucket.dataset.ing;
//     const custom_label_pic = this.bucket.dataset.clabel;

//     const local_storage_cards = JSON.parse(localStorage.getItem("cards")) || [];
//     const product_id = data.variants[0].id || data.id;
//     const cardHTML = this.bucket.getHTMLCard(data);

//     const cardEl = document.querySelectorAll(".product-card")[index];
//     cardEl.setAttribute("data-id", product_id);
//     cardEl.innerHTML = cardHTML;
//     cardEl.classList.remove("empty");
//     cardEl.classList.add("food-card");
//     cardEl.setAttribute(
//       "data-price",
//       Shopify.formatMoney(data.price, "{{amount}}")
//     );
//     cardEl.setAttribute("data-url", data.url);

//     const card = {
//       featured_image: image,
//       id: data.variants[0].id || data.id,
//       price: data.price,
//       title: data.title,
//       url: data.url,
//       labelpic: custom_label_pic,
//       ingredient: ingredients_image,
//       nutrifact: nutrifacts_image,
//     };

//     local_storage_cards.push(card);
//     localStorage.setItem("cards", JSON.stringify(local_storage_cards));

//     this.bucket.updateImageContainers(local_storage_cards);
//     this.bucket.updatePrice();

//     if (typeof alertify !== "undefined") {
//       alertify.success("Product added to the bucket");
//     }
//   });
// }

//working version
// connectedCallback() {
//   this.addEventListener("click", async () => {
//     const url = this.bucket.dataset.url + "/products.js";
//     const response = await fetch(url);
//     const data = await response.json();

//     const variant = data.variants[0];
//     const variantId = variant.id;

//     // ---- INVENTORY CHECK ------------------------------------------------
//     if (window.InventoryCheck) {
//       const check = await window.InventoryCheck.canAddToInventory(variantId, 1);
//       console.log('[bucket] inventory check:', check);

//       if (!check.ok) {
//         if (typeof alertify !== "undefined") {
//           alertify.error(
//             check.stock <= 0
//               ? "This product is out of stock."
//               : `Only ${check.remaining} left — you already have ${check.committed} in your bucket or cart.`
//           );
//         }
//         return;
//       }
//     } else {
//       console.warn('[bucket] InventoryCheck not loaded, skipping stock check');
//     }
//     // ---------------------------------------------------------------------

//     const image = data.featured_image;
//     const index = this.bucket.getEmptyCard();
//     if (index < 0) return;

//     const nutrifacts_image = this.bucket.dataset.nutrifact;
//     const ingredients_image = this.bucket.dataset.ing;
//     const custom_label_pic = this.bucket.dataset.clabel;

//     const local_storage_cards = JSON.parse(localStorage.getItem("cards")) || [];
//     const product_id = data.variants[0].id || data.id;
//     const cardHTML = this.bucket.getHTMLCard(data);

//     const cardEl = document.querySelectorAll(".product-card")[index];
//     cardEl.setAttribute("data-id", product_id);
//     cardEl.innerHTML = cardHTML;
//     cardEl.classList.remove("empty");
//     cardEl.classList.add("food-card");
//     cardEl.setAttribute(
//       "data-price",
//       Shopify.formatMoney(data.price, "{{amount}}")
//     );
//     cardEl.setAttribute("data-url", data.url);

//     const card = {
//       featured_image: image,
//       id: data.variants[0].id || data.id,
//       price: data.price,
//       title: data.title,
//       url: data.url,
//       labelpic: custom_label_pic,
//       ingredient: ingredients_image,
//       nutrifact: nutrifacts_image,
//     };

//     local_storage_cards.push(card);
//     localStorage.setItem("cards", JSON.stringify(local_storage_cards));

//     this.bucket.updateImageContainers(local_storage_cards);
//     this.bucket.updatePrice();

//     if (typeof alertify !== "undefined") {
//       alertify.success("Product added to the bucket");
//     }
//   });
// }




connectedCallback() {
  this.addEventListener("click", () => {
    // Server-rendered disabled state (coming soon / sold out) — bail before
    // queuing anything so a stray CSS override can't re-enable purchasing.
    if (this.hasAttribute('disabled') || this.getAttribute('aria-disabled') === 'true') {
      return;
    }
    // Queue this click behind any add-to-bucket click still in flight —
    // see addToBucketMutex above for why.
    addToBucketMutex = addToBucketMutex.then(async () => {
    // ---- DETERMINE REQUESTED QUANTITY -----------------------------------
    // If this button is inside a quick-view modal, read the modal's qty input.
    // Otherwise (regular grid card), default to 1.
    const modalAncestor = this.closest('quick-add-modal, .quick-add-modal, [aria-modal="true"]');
    let requestedQty = 1;
    if (modalAncestor) {
      const qtyInput = modalAncestor.querySelector('input[name="quantity"], .quantity__input');
      const parsed = parseInt(qtyInput?.value || '1', 10);
      if (!isNaN(parsed) && parsed >= 1) requestedQty = parsed;
    }
    console.log('[bucket] requested quantity:', requestedQty);
    // ---------------------------------------------------------------------

    // Cache-busted + no-store: this must be the real current product state,
    // not a browser-cached copy, since we check live inventory against it.
    const url = this.bucket.dataset.url + `/products.js?_=${Date.now()}`;
    const response = await fetch(url, { cache: "no-store" });
    const data = await response.json();

    const variant = data.variants[0];
    const variantId = variant.id;

    // ---- INVENTORY CHECK (for full requested quantity) ------------------
    // Real-time stock via the Storefront API — the /products.js fetch above
    // does NOT include a usable inventory count (Shopify simply omits it),
    // so the live variant has to come from a separate GraphQL call.
    if (window.InventoryCheck) {
      const liveVariant = await window.InventoryCheck.fetchLiveVariant(variantId);
      const check = await window.InventoryCheck.canAddToInventory(variantId, requestedQty, liveVariant);
      console.log('[bucket] inventory check:', check);

      if (!check.ok) {
        if (typeof alertify !== "undefined") {
          alertify.error(
            check.stock <= 0
              ? "This product is out of stock."
              : `Only ${check.remaining} left — you tried to add ${requestedQty}, but ${check.committed} are already in your bucket or cart.`
          );
        }
        return;
      }
    }
    // ---------------------------------------------------------------------

    // ---- BUCKET SLOT CHECK ----------------------------------------------
    // Count how many empty slots are available before we start
    const emptySlots = document.querySelectorAll(".product-card.empty").length;
    if (emptySlots === 0) {
      if (typeof alertify !== "undefined") {
        alertify.error("Your bucket is full.");
      }
      return;
    }
    const willAdd = Math.min(requestedQty, emptySlots);
    if (willAdd < requestedQty) {
      if (typeof alertify !== "undefined") {
        alertify.warning(`Bucket only has ${emptySlots} slot(s) left — adding ${emptySlots} instead of ${requestedQty}.`);
      }
    }
    // ---------------------------------------------------------------------

    const image = data.featured_image;
    const nutrifacts_image = this.bucket.dataset.nutrifact;
    const ingredients_image = this.bucket.dataset.ing;
    const custom_label_pic = this.bucket.dataset.clabel;

    const local_storage_cards = JSON.parse(localStorage.getItem("cards")) || [];
    const product_id = data.variants[0].id || data.id;

    // ---- ADD `willAdd` COPIES OF THE PRODUCT TO THE BUCKET --------------
    for (let i = 0; i < willAdd; i++) {
      const index = this.bucket.getEmptyCard();
      if (index < 0) break; // safety — should never trigger thanks to willAdd

       const cardHTML = this.bucket.getHTMLCard(data);
      //const cardHTML = this.bucket.getHTMLCard({ ...data, labelpic: custom_label_pic });
      const cardEl = document.querySelectorAll(".product-card")[index];
      cardEl.setAttribute("data-id", product_id);
      cardEl.innerHTML = cardHTML;
      cardEl.classList.remove("empty");
      cardEl.classList.add("food-card");
      cardEl.setAttribute(
        "data-price",
        Shopify.formatMoney(data.price, "{{amount}}")
      );
      cardEl.setAttribute("data-url", data.url);

      local_storage_cards.push({
        featured_image: image,
        id: data.variants[0].id || data.id,
        price: data.price,
        title: data.title,
        url: data.url,
        labelpic: custom_label_pic,
        ingredient: ingredients_image,
        nutrifact: nutrifacts_image,
      });
    }
    // ---------------------------------------------------------------------

    localStorage.setItem("cards", JSON.stringify(local_storage_cards));
    this.bucket.updateImageContainers(local_storage_cards);
    this.bucket.updatePrice();

    if (typeof alertify !== "undefined") {
      alertify.success(
        willAdd === 1
          ? "Product added to the bucket"
          : `${willAdd} products added to the bucket`
      );
    }
    }).catch((err) => {
      // Keep the mutex chain alive even if one click's handler throws —
      // otherwise every click after it would silently stop working.
      console.error('[AddToBucket] click handler error:', err);
    });
  });
}

  // -------------------------------------------------------------------------
  //  Populate the label preview from the current cards[] in localStorage.
  //  Writes into the three correct containers, does NOT destroy structure.
  // -------------------------------------------------------------------------
  updateImageContainers(cards) {
    const frontGrid  = document.querySelector(".label-front-img");
    const thumbsGrid = document.querySelector(".gallery-thumbs-grid");
    const stack      = document.querySelector(".image-stack-container");

    if (!frontGrid || !thumbsGrid || !stack) {
      console.warn("[AddToBucket] Label containers not found in DOM — skipping preview update.");
      return;
    }

    // --- FRONT LABEL: product imagery (labelpic, falls back to featured_image)
    frontGrid.innerHTML = "";
    cards.forEach((card) => {
      const img = document.createElement("img");
      img.width = 80;
      img.height = 80;
      img.src = AddToBucket.normalizeUrl(card.labelpic || card.featured_image);
      img.alt = card.title || "";
      frontGrid.appendChild(img);
    });

    // --- BACK LABEL: nutrition thumbnails
    thumbsGrid.innerHTML = "";
    cards.forEach((card) => {
      if (!card.nutrifact) return;
      const img = document.createElement("img");
      img.width = 80;
      img.height = 80;
      img.src = AddToBucket.normalizeUrl(card.nutrifact);
      img.alt = (card.title || "") + " nutrition";
      thumbsGrid.appendChild(img);
    });

    // --- BACK LABEL: ingredient strips
    stack.innerHTML = "";
    cards.forEach((card, index) => {
      if (!card.ingredient) return;
      const img = document.createElement("img");
      img.src = AddToBucket.normalizeUrl(card.ingredient);
      img.alt = (card.title || "") + " ingredients";
      img.className = `stack-img img-${index + 1}`;
      stack.appendChild(img);
    });
  }

  // Shopify CDN URLs sometimes come back as "//cdn.shopify.com/..." — ensure
  // they have a scheme so <img src> resolves correctly, especially when
  // rendered into a PDF by html2pdf/html2canvas.
  static normalizeUrl(url) {
    if (!url) return "";
    if (url.startsWith("//")) return "https:" + url;
    return url;
  }

  getEmptyCard() {
    const cards = document.querySelectorAll(".product-card");
    return Array.from(cards).findIndex((elem) =>
      elem.classList.contains("empty")
    );
  }

  getHTMLCard(data) {
    return `
      <close-button class="close-button">
        <svg width="41" height="41" viewBox="0 0 41 41" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g filter="url(#filter0_d_14460_1789)">
            <rect x="3.23389" y="3.17285" width="34.2008" height="34.2008" rx="17.1004" fill="#F05252"/>
            <rect x="4.65892" y="4.59789" width="31.3508" height="31.3508" rx="15.6754" stroke="#121212" stroke-opacity="0.11" stroke-width="2.85007"/>
            <path d="M13.2437 27.3635L27.4245 13.1826" stroke="white" stroke-width="2.5025"/>
            <path d="M27.4248 27.3635L13.244 13.1826" stroke="white" stroke-width="2.5025"/>
          </g>
          <defs>
            <filter id="filter0_d_14460_1789" x="0.383817" y="0.322782" width="39.9008" height="39.9008" filterUnits="userSpaceOnUse" color-interpolation-filters="sRGB">
              <feFlood flood-opacity="0" result="BackgroundImageFix"/>
              <feColorMatrix in="SourceAlpha" type="matrix" values="0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 0 127 0" result="hardAlpha"/>
              <feMorphology radius="2.85007" operator="dilate" in="SourceAlpha" result="effect1_dropShadow_14460_1789"/>
              <feOffset/>
              <feComposite in2="hardAlpha" operator="out"/>
              <feColorMatrix type="matrix" values="0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 1 0"/>
              <feBlend mode="normal" in2="BackgroundImageFix" result="effect1_dropShadow_14460_1789"/>
              <feBlend mode="normal" in="SourceGraphic" in2="effect1_dropShadow_14460_1789" result="shape"/>
            </filter>
          </defs>
        </svg>
      </close-button>
      <div class="food-image">
      <img src="${data.featured_image}" alt="${data.title}">
      </div>
      <div class="card_content">
        <div class="food-title">${data.title}</div>
        <div class="food-price">${Shopify.formatMoney(data.price, "${{amount}}")}</div>
      </div>
    `;
  }

  removeProductHTML() {
    return `
      <div class="product-card empty">
        <div class="add-icon">
          <svg width="25" height="25" viewBox="0 0 35 35" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.5713 0.122559V34.4165" stroke="currentColor" stroke-width="3"/>
            <path d="M0.424316 17.2695L34.7183 17.2695" stroke="currentColor" stroke-width="3"/>
          </svg>
        </div>
        <div class="add-text">Add product to bucket</div>
      </div>`;
  }

  /**
   * Counts product cards that are not empty slots.
   */
  getFilledSlotCount() {
    let filled = 0;
    document.querySelectorAll(".product-card").forEach((elem) => {
      if (!elem.classList.contains("empty")) filled += 1;
    });
    return filled;
  }

  updatePrice() {
    let price = 0;
    const filled = this.getFilledSlotCount();

    document.querySelectorAll(".product-card").forEach((elem) => {
      if (!elem.classList.contains("empty")) {
        const raw = (elem.dataset.price || "").replace(/[^0-9.]/g, "");
        price += parseFloat(raw) || 0;
      }
    });

    const bucket_price = document.querySelector(".bucket_price");
    if (bucket_price) bucket_price.innerHTML = `$${price.toFixed(2)}`;

    const slotCount = document.querySelector(".bucket-slot-count__current");
    if (slotCount) slotCount.textContent = filled;

    const checkout_button = document.querySelector(".checkout-btn");
    if (checkout_button) {
      // Hard rule: enable only when all 6 slots are filled.
      if (filled >= 6) {
        checkout_button.removeAttribute("disabled");
      } else {
        checkout_button.setAttribute("disabled", "disabled");
      }
    }

    return filled;
  }

  /**
   * True when every bucket slot has a product selected.
   * Case: Empty Bucket and item removal must keep checkout disabled until 6 filled.
   */
  isBucketFull() {
    return this.getFilledSlotCount() >= 6;
  }
}
customElements.define("add-to-bucket", AddToBucket);


// ===========================================================================
//  Checkout handler — fixed
// ===========================================================================
//  Changes vs. original:
//  1. Populates .label-front-img, .gallery-thumbs-grid, and
//     .image-stack-container via AddToBucket.updateImageContainers
//     (single source of truth) instead of duplicating the logic inline.
//  2. Adds a submit-lock so double-clicks don't create duplicate products.
//  3. Normalizes protocol-relative CDN URLs before rendering into the PDF.
//  4. Staged progress copy below Add to Cart (SfsByobCheckoutProgress).
// ===========================================================================
function waitForImages(root, timeoutMs = 8000) {
  const pending = Array.from(root.querySelectorAll("img")).map((img) =>
    img.complete && img.naturalWidth > 0
      ? Promise.resolve()
      : new Promise((res) => {
          img.addEventListener("load", res, { once: true });
          img.addEventListener("error", res, { once: true });
        })
  );
  return Promise.race([
    Promise.all(pending),
    new Promise((res) => setTimeout(res, timeoutMs)),
  ]);
}

const checkout_button = document.querySelector(".checkout-btn");

if (checkout_button) {
  let isSubmitting = false;

  function resetCheckoutUi() {
    const spinner = checkout_button.querySelector(".loading__spinner");
    const bucketText = checkout_button.querySelector(".bucket_text");
    if (spinner) spinner.classList.add("hidden");
    if (bucketText) bucketText.style.display = "block";
    isSubmitting = false;
    const bucketElem = document.querySelector("add-to-bucket");
    if (bucketElem && typeof bucketElem.updatePrice === "function") {
      bucketElem.updatePrice();
    } else {
      checkout_button.setAttribute("disabled", "disabled");
    }
    if (window.SfsByobCheckoutProgress) {
      window.SfsByobCheckoutProgress.stop();
    }
  }

  async function verifyBucketInventory(cards) {
    if (!window.InventoryCheck || !cards.length) {
      return { ok: true };
    }

    const counts = {};
    const titles = {};
    cards.forEach(function (card) {
      const id = Number(card.id);
      counts[id] = (counts[id] || 0) + 1;
      if (!titles[id]) titles[id] = card.title || `variant ${id}`;
    });

    for (const variantId in counts) {
      if (!Object.prototype.hasOwnProperty.call(counts, variantId)) continue;
      const id = Number(variantId);
      const qty = counts[variantId];
      // Live inventory, straight from Shopify — not the page-load-time snapshot.
      // includeBucket: false — `qty` here already IS this bucket's own count
      // for this variant, so it must not also be added back in as
      // "already committed" (that double-counts the same items against
      // themselves and would block finalizing a bucket that exactly uses
      // the last unit in stock).
      const liveVariant = await window.InventoryCheck.fetchLiveVariant(id);
      const check = await window.InventoryCheck.canAddToInventory(id, qty, liveVariant, { includeBucket: false });
      if (!check.ok) {
        return { ok: false, check: check, variantId: id, title: titles[id] };
      }
    }

    return { ok: true };
  }

  checkout_button.addEventListener("click", async () => {
    if (isSubmitting) return;

    const filledSlots = document.querySelectorAll(".product-card:not(.empty)").length;
    if (filledSlots < 6) {
      checkout_button.setAttribute("disabled", "disabled");
      return;
    }

    const bucketInput = document.querySelector("#bucket-name");
    const main_title = document.querySelector(".cstm-bucket-title");

    // --- Validate bucket name ---
    // No name entered: focus the inline field and pulse a glow around it
    // instead of the old popup-modal flow (modal removed).
    if (!bucketInput || bucketInput.value.trim() === "") {
      const field = bucketInput ? bucketInput.closest(".bucket-name-field") : null;

      if (bucketInput) {
        bucketInput.focus();
        bucketInput.scrollIntoView({ behavior: "smooth", block: "center" });
      }

      if (field) {
        field.classList.remove("is-glowing");
        void field.offsetWidth; // restart the animation on repeated clicks
        field.classList.add("is-glowing");
        field.addEventListener(
          "animationend",
          () => field.classList.remove("is-glowing"),
          { once: true }
        );
      }

      return;
    }

    const bucketName = bucketInput.value.trim();
    if (main_title) main_title.textContent = bucketName;

    // --- Populate label preview from localStorage ---
    const cardss = JSON.parse(localStorage.getItem("cards")) || [];

 const pdfTitle = document.querySelector(".label-h1");
const blTitle  = document.querySelector(".label-h1s");
const labelH1d = document.querySelector(".label-h1d");
if (pdfTitle) pdfTitle.innerHTML = bucketName;
if (blTitle)  blTitle.innerHTML  = bucketName;
if (labelH1d) labelH1d.innerHTML = bucketName;

 
    // Single source of truth for populating the three label containers.
    const bucketElem = document.querySelector("add-to-bucket");
    if (bucketElem) {
      bucketElem.updateImageContainers(cardss);
    }

    // --- Build properties payload ---
    const _local_cards = cardss;
    const spinner    = checkout_button.querySelector(".loading__spinner");
    const bucketText = checkout_button.querySelector(".bucket_text");

    if (spinner) spinner.classList.remove("hidden");
    checkout_button.setAttribute("disabled", "disabled");
    if (bucketText) bucketText.style.display = "none";
    isSubmitting = true;

    if (window.SfsByobCheckoutProgress) {
      window.SfsByobCheckoutProgress.start();
    }

    const inventoryResult = await verifyBucketInventory(_local_cards);

    if (!inventoryResult.ok) {
      resetCheckoutUi();
      if (typeof alertify !== "undefined") {
        const check = inventoryResult.check;
        const productName = inventoryResult.title || "One of your selections";
        alertify.error(
          check.stock <= 0
            ? `"${productName}" is out of stock. Please update your bucket.`
            : `"${productName}" only has ${check.remaining} left in stock. Please update your bucket.`
        );
      }
      return;
    }

    const cards = document.querySelectorAll(".product-card");
    const selectedIds = [];
    let totalPrice = 0;

    cards.forEach((elem) => {
      const id = elem.dataset.id;
      const raw = (elem.dataset.price || "").replace(/[^0-9.]/g, "");
      const price = parseFloat(raw) || 0;
      if (id) {
        selectedIds.push(id);
        totalPrice += price;
      }
    });

    const formattedPrice = totalPrice.toLocaleString(undefined, {
      style: "currency",
      currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || "USD",
    });

    const properties = {
      Custom_Bucket: bucketName,
      _bundle_products: selectedIds.join(", "),
      _bundle_total: formattedPrice,
    };

   _local_cards.forEach((data, index) => {
  properties[`Pouch ${index + 1}`] = data.title;
  properties[`_product_image_${index + 1}`] = data.featured_image;
  properties[`_product_url_${index + 1}`] = data.url;
});

// Variant IDs of every pouch in this bucket — used for inventory checks.
// Underscore prefix keeps it hidden from the customer-facing cart UI.
properties._contents = _local_cards.map(c => c.id).join(',');

    // --- Create product on the backend, then generate PDF + submit ---
    fetch("https://custom-bucket-fa71d360efeb.herokuapp.com/create-product", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        title: bucketName,
        body_html: "Created via frontend JS",
        vendor: "Shopify Vendor",
        product_type: "Custom",
        price: formattedPrice,
    image_url: "https://cdn.shopify.com/s/files/1/0772/0121/1682/files/CustomBucketCutOut_-_thumbnail_image_for_BYOB.png?v=1790017892",
      }),
    })
      .then((response) => response.json())
      .then(async (response) => {
        window.scrollTo(0, 0);

        if (window.SfsByobCheckoutProgress) {
          window.SfsByobCheckoutProgress.holdFinalStep();
        }

        try {
          if (typeof html2pdf === "undefined") {
            throw new Error("html2pdf library is not loaded");
          }

          const invoiceLabel = document.getElementById("invoice-wrapper");
          // Make sure the popup is visible so html2canvas can measure it.
          const popup = document.getElementById("invoice-popup");
          const wasHidden = popup && !popup.classList.contains("show");
          if (wasHidden) {
            popup.style.visibility = "hidden";
            popup.classList.add("show");
          }
await window.fitLabelH1dForCapture();
await waitForImages(invoiceLabel); 

          const optp = {
            margin: [0, 0, 0, 0],
            image: { type: "jpeg", quality: 1 },
            html2canvas: {
              scale: 3.125,
              useCORS: true,
              allowTaint: true,
              scrollX: 0,
              scrollY: 0,
              windowWidth: document.documentElement.scrollWidth,
              windowHeight: document.documentElement.scrollHeight,
            },
            jsPDF: { unit: "in", format: [5.5, 8.5], orientation: "portrait" },
          };

          // const pdfBlob = await html2pdf().set(optp).from(invoiceLabel).toPdf().output("blob");

          const MAX_PAGES = 2;

const pdfBlob = await html2pdf()
  .set(optp)
  .from(invoiceLabel)
  .toPdf()
  .get("pdf")
  .then((pdf) => {
    let total = pdf.internal.getNumberOfPages();
    if (total > MAX_PAGES) {
      console.warn(`Label rendered ${total} pages; trimming to ${MAX_PAGES}.`);
    }
    while (total > MAX_PAGES) {
      pdf.deletePage(total);
      total--;
    }
  })
  .output("blob");

          if (wasHidden && popup) {
            popup.classList.remove("show");
            popup.style.visibility = "";
          }

          const pdfFile = new File([pdfBlob], "custombucket.pdf", { type: "application/pdf" });
          const dataTransfer = new DataTransfer();
          dataTransfer.items.add(pdfFile);

          const hiddenFileInput = document.getElementById("hidden-pdf-upload");
          hiddenFileInput.files = dataTransfer.files;

          const hiddenProductId = document.getElementById("hidden-product-id");
          hiddenProductId.value = response.product.variants[0].id;

          const nform = document.getElementById("hidden-pdf-form");
          nform.querySelectorAll(".dynamic-property").forEach((el) => el.remove());

          for (const key in properties) {
            const input = document.createElement("input");
            input.type = "hidden";
            input.name = `properties[${key}]`;
            input.value = properties[key];
            input.classList.add("dynamic-property");
            nform.appendChild(input);
          }

          if (main_title) main_title.textContent = "";
          const cstmTitle = document.querySelector(".cstm-bucket-title");
          if (cstmTitle) cstmTitle.textContent = "";
          if (bucketInput) bucketInput.value = "";

          localStorage.removeItem("cards");
          localStorage.removeItem("_edit_bundle");
          localStorage.removeItem("_edit_bundle_name");

          // Form submit navigates the page — anything after this won't run.
          nform.submit();
        } catch (error) {
          console.error("Error generating PDF:", error);
          resetCheckoutUi();
          if (typeof alertify !== "undefined") {
            alertify.error("Something went wrong generating your bucket. Please try again.");
          }
        }
      })
      .catch((error) => {
        console.error("Error creating product:", error);
        resetCheckoutUi();
      });
  });
}

class CloseButton extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener("click", (event) => {
      event.stopPropagation();
      //  console.log("Close button clicked");

      let productCard = this.closest(".product-card");
      if (!productCard) {
        console.error("Product card not found.");
        return;
      }

      let popup = document.querySelector("#remove-confirmation-popup");
      if (!popup) {
        console.error("Popup element not found.");
        return;
      }

      // Show popup and store product ID
      popup.classList.add("show");
      popup.setAttribute("data-target-product", productCard.dataset.id);
    });
  }
}

// Define the custom element
customElements.define("close-button", CloseButton);

// Handle clicks on the document
document.body.addEventListener("click", (event) => {
  let popup = document.querySelector("#remove-confirmation-popup");
  if (!popup) return;

  // Confirm and remove the product
  if (event.target.matches("#confirm-remove")) {
    let productId = popup.getAttribute("data-target-product");

    if (!productId) {
      console.error("Product ID not found.");
      return;
    }

    // Convert productId to a number in case it's stored as a number
    productId = parseInt(productId, 10);

    let productCard = document.querySelector(
      `.product-card[data-id='${productId}']`
    );
    if (!productCard) {
      console.error(`Product card not found for removal (ID: ${productId}).`);
      return;
    }

    // Retrieve local storage data
    let local_cards = JSON.parse(localStorage.getItem("cards")) || [];

    // Find the index of the first matching product and remove it
    let index = local_cards.findIndex(
      (card) => parseInt(card.id, 10) === productId
    );

    if (index !== -1) {
      local_cards.splice(index, 1); // Remove only the first occurrence
      //  console.log(`Removed product at index ${index}`);
    } else {
      console.warn("No matching product found in local storage.");
    }
    //console.log("remove_card" , local_cards)
    // Update local storage
    localStorage.setItem("cards", JSON.stringify(local_cards));

    // Remove the product from the DOM
    productCard.remove();

    // Close the popup
    popup.classList.remove("show");

    let bucketElem = document.querySelector("add-to-bucket");
    if (bucketElem) {
      let parser = new DOMParser();
      let empty_card = bucketElem.removeProductHTML();
      let doc = parser.parseFromString(empty_card, "text/html");
      let parsedContent = doc.body.firstElementChild;
      if (parsedContent) {
        let bundles = document.querySelector(".bundle--products");
        bundles.appendChild(parsedContent);
      }
      bucketElem.updatePrice();
    }
  }

  if (
    event.target.matches("#cancel-remove") ||
    event.target.closest(".popup-close")
  ) {
    // console.log("Popup close button clicked");
    popup.classList.remove("show");
  }
});
 

let local_cards = JSON.parse(localStorage.getItem("cards")) || [];
let __edit_bundle = JSON.parse(localStorage.getItem("_edit_bundle"));

//console.log("__edit_bundle", __edit_bundle);

const params = new URLSearchParams(window.location.search);

if (params.has("edit_bundle") && __edit_bundle) {
  let bucketElem = document.querySelector("add-to-bucket");
  let bundles = document.querySelector(".bundle--products");

  let lengths = bundles ? bundles.querySelectorAll(".product-card").length : 0;
  // console.log("Bundles Product Card Lengths:", lengths);

  if (bucketElem) {
    (async function () {
      let local_storage_cards = [];
      for (let index = 0; index < __edit_bundle.length; index++) {
        let handle = __edit_bundle[index];
        let url = `${handle}/products.js`;

        //console.log("Fetching URL:", url);

        // try {
        //   let response = await fetch(url);
        //   if (!response.ok) throw new Error("Fetch failed for: " + url);

        //   let productData = await response.json(); // rename from 'data' to 'productData'
        //   let cardHTML = bucketElem.getHTMLCard(productData);
        //   let product_id = productData.variants[0]?.id || productData.id;
        //   //console.log('product data testing', productData);
        //   // Update LocalStorage
        //   //console.log(productData)
        //   let card = {
        //     featured_image: productData.featured_image,
        //     id: productData.variants[0]?.id || productData.id,
        //     price: productData.price,
        //     title: productData.title,
        //     url: productData.url,
        //   };
        //   //console.log(card.id)
        //   local_storage_cards.push(card);
        //   localStorage.setItem("cards", JSON.stringify(local_storage_cards));

        //   // Update the UI
        //   let productCards = document.querySelectorAll(".product-card");
        //   if (productCards[index]) {
        //     productCards[index].setAttribute("data-id", product_id);
        //     productCards[index].innerHTML = cardHTML;
        //     productCards[index].classList.remove("empty");
        //     productCards[index].classList.add("food-card");
        //     productCards[index].setAttribute(
        //       "data-price",
        //       Shopify.formatMoney(productData.price, "{{amount}}")
        //     );
        //     productCards[index].setAttribute("data-url", productData.url);
        //     productCards[index].setAttribute("data-title", productData.title);
        //   }
        // } catch (error) {
        //   console.error("Error fetching product:", error);
        // }


        try {
  let response = await fetch(url);
  if (!response.ok) throw new Error("Fetch failed for: " + url);

  let productData = await response.json();
  let cardHTML = bucketElem.getHTMLCard(productData);
  let product_id = productData.variants[0]?.id || productData.id;

  // Fetch the product page to extract metafield-backed data attributes
  // (data-clabel / data-ing / data-nutrifact on <add-to-bucket>)
  let cardMetafields = { labelpic: null, ingredient: null, nutrifact: null };
  try {
    const pageHtml = await fetch(handle).then(r => r.text());
    const parser = new DOMParser();
    const doc = parser.parseFromString(pageHtml, "text/html");
    const bucketEl = doc.querySelector("add-to-bucket");
    if (bucketEl) {
      cardMetafields.labelpic = bucketEl.dataset.clabel || null;
      cardMetafields.ingredient = bucketEl.dataset.ing || null;
      cardMetafields.nutrifact = bucketEl.dataset.nutrifact || null;
    } else {
      console.warn("[edit-bundle] <add-to-bucket> not found on page for", handle);
    }
  } catch (e) {
    console.warn("[edit-bundle] failed to fetch metafields for", handle, e);
  }

  let card = {
    featured_image: productData.featured_image,
    id: productData.variants[0]?.id || productData.id,
    price: productData.price,
    title: productData.title,
    url: productData.url,
    labelpic: cardMetafields.labelpic,
    ingredient: cardMetafields.ingredient,
    nutrifact: cardMetafields.nutrifact,
  };

  local_storage_cards.push(card);
  localStorage.setItem("cards", JSON.stringify(local_storage_cards));

  // Update the UI
  let productCards = document.querySelectorAll(".product-card");
  if (productCards[index]) {
    productCards[index].setAttribute("data-id", product_id);
    productCards[index].innerHTML = cardHTML;
    productCards[index].classList.remove("empty");
    productCards[index].classList.add("food-card");
    productCards[index].setAttribute(
      "data-price",
      Shopify.formatMoney(productData.price, "{{amount}}")
    );
    productCards[index].setAttribute("data-url", productData.url);
    productCards[index].setAttribute("data-title", productData.title);
  }
} catch (error) {
  console.error("Error fetching product:", error);
}
      }
      let bucketInput = document.querySelector("#bucket-name");
      if (!bucketInput || bucketInput.value.trim() === "") {
        if (JSON.parse(localStorage.getItem("_edit_bundle_name"))) {
          const bundleElem = document.querySelector("bundle-products");
          const bucketText = document.querySelector(".bucket_with_name .cstm-bucket-title");

          bundleElem.setAttribute(
            "property",
            JSON.parse(localStorage.getItem("_edit_bundle_name"))
          );
          if (bucketText) {
            bucketText.textContent = `${JSON.parse(
              localStorage.getItem("_edit_bundle_name")
            )}`;
          }

          bucketInput.value = JSON.parse(
            localStorage.getItem("_edit_bundle_name")
          );
        }
      }

      bucketElem.updatePrice();
      // console.log("Updated Local Storage Cards:", JSON.parse(localStorage.getItem("cards")));
    })();
  }
} else if (local_cards) {
  let bucketElem = document.querySelector("add-to-bucket");
  let bundles = document.querySelector(".bundle--products");

  let lengths = bundles ? bundles.length : 0;
  // console.log(lengths);

  if (bucketElem) {
    local_cards.forEach((data, index) => {
      let newDiv = document.createElement("div");
      let product_id = data.id;
      let cardHTML = bucketElem.getHTMLCard(data);

      let productCards = document.querySelectorAll(".product-card");
      if (productCards[index]) {
        productCards[index].setAttribute("data-id", product_id);
        productCards[index].innerHTML = cardHTML;
        productCards[index].classList.remove("empty");
        productCards[index].classList.add("food-card");
        productCards[index].setAttribute(
          "data-price",
          Shopify.formatMoney(data.price, "{{amount}}")
        );
        productCards[index].setAttribute("data-url", data.url);
        productCards[index].setAttribute("data-title", data.title);
      }
    });

    bucketElem.updatePrice();
    // console.log(local_cards);
  }
}

class SavedLater extends HTMLElement {
  constructor() {
    super();
    this.saved = this;
  }

  connectedCallback() {
    let item = JSON.parse(localStorage.getItem("saved_for_later")) || [];
    if (item.length > 0) {
      item.forEach((product, index) => {
        let p = this.UpdateCard(product); // correctly call instance method
        this.appendChild(p);
      });
    } else {
    }

    let removed_saved_product = document.querySelectorAll(
      ".removed_saved_product"
    );
    if (removed_saved_product.length > 0) {
      removed_saved_product.forEach(function (remove_product) {
        remove_product.addEventListener("click", function (event) {
          let key = remove_product.closest(".product-card").dataset.key;

          // Correct way: check if any object has matching key
          let exists = item.some((product) => product.key === key);

          if (exists) {
            // Remove item with matching key
            item = item.filter((product) => product.key !== key);

            // Save updated array
            localStorage.setItem("saved_for_later", JSON.stringify(item));

            // Remove the product card from DOM
            remove_product.closest(".product-card").remove();
          } else {
            // console.log("test: key not found", key, item);
          }
        });
      });
    }

    let saved_add_to_cart = document.querySelectorAll(".saved_add_to_cart");
    if (saved_add_to_cart.length > 0) {
      saved_add_to_cart.forEach(function (add_to_cart) {
        add_to_cart.addEventListener("click", function (event) {
          let formData = {};
          if (
            add_to_cart.closest(".product-card").hasAttribute("data-bundle")
          ) {
            let selectedIds = [];
            let totalPrice = 0;

            // cards.forEach(function (elem) {
            //   let id = elem.dataset.id;
            //   let price = parseFloat(elem.dataset.price || "0");

            //   if (id) {
            //     selectedIds.push(id);
            //     totalPrice += price;
            //   }
            // });

            let formattedPrice = totalPrice.toLocaleString(undefined, {
              style: "currency",
              currency: Shopify.currency.active || "USD",
            });
            let bucketName = add_to_cart
              .closest(".product-card")
              .getAttribute("data-bundle");

            let properties = {
              Custom_Bucket: bucketName,
            };
            let products_images = add_to_cart
              .closest(".product-card")
              .querySelectorAll(".ah-cart-img .saved_item_image");
            products_images.forEach((data, index) => {
              properties[`_product_image_${index + 1}`] =
                data.getAttribute("src");
            });

            let props_title = add_to_cart
              .closest(".product-card")
              .querySelectorAll(".all-bundle-titles .prop-title");
            props_title.forEach((data, index) => {
              properties[`Pouch ${index + 1}`] = data.innerText;

              // console.log("properties", properties)
            });
            let props_url = add_to_cart
              .closest(".product-card")
              .querySelectorAll(".handles_url li");
            props_url.forEach((data, index) => {
              properties[`_product_url_${index + 1}`] = data.innerText.trim();

              // console.log("properties", properties)
            });
            const bundleProductId = "46860366184738";
            formData = {
              items: [
                {
                  id: bundleProductId,
                  quantity: 1,
                  properties: properties,
                },
              ],
            };
          } else {
            let bundleProductId =
              add_to_cart.closest(".product-card").dataset.id;
            formData = {
              items: [
                {
                  id: bundleProductId,
                  quantity: 1,
                },
              ],
            };
          }
          //console.log(formData, "formData")

          fetch(window.Shopify.routes.root + "cart/add.js", {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(formData),
          })
            .then((response) => response.json())
            .then((response) => {
              //console.log("Cart response:", response);

              // if (bucketInput) bucketInput.value = "";
              // if (main_title) main_title.textContent = "";

              // localStorage.removeItem("cards");

              // setTimeout(() => {
              //   if (spinner) spinner.classList.add("hidden");
              //   checkout_button.removeAttribute("disabled");
              //   if (bucketText) bucketText.style.display = "block";
              // }, 300);

              setTimeout(() => {
                window.location.href = "/cart";
              }, 700);
            })
            .catch((error) => {
              console.error("Error:", error);
            });
        });
      });
    }
  }

  UpdateCard(data) {
    let wrapper = document.createElement("div");
    wrapper.classList.add("product-card");
    wrapper.setAttribute("data-id", data.id);
    wrapper.setAttribute("data-key", data.key);
    data.bundle !== ""
      ? wrapper.setAttribute("data-bundle", data.bundle)
      : null;

    let inner_wrapper = document.createElement("div");
    inner_wrapper.classList.add("product-content");
    wrapper.appendChild(inner_wrapper);

    let saved_iem_image = document.createElement("div");
    inner_wrapper.appendChild(saved_iem_image);

    if (data.images.length > 0) {
      saved_iem_image.classList.add("ah-cart-img");
      data.images.forEach(function (image) {
        let img = document.createElement("img");
        img.setAttribute("src", image);
        img.classList.add("saved_item_image");
        saved_iem_image.appendChild(img); // Append to the image container instead of inner_wrapper
      });
      //console.log("Images appended:", saved_iem_image);
    } else {
      saved_iem_image.classList.add("ah-cart-main");
      let img = document.createElement("img");
      img.setAttribute("src", data.image);
      img.classList.add("saved_item_image__single");
      saved_iem_image.appendChild(img);
    }

    // Product Details

    let productDetail = document.createElement("div");
    productDetail.classList.add("product-details");
    inner_wrapper.appendChild(productDetail);
    let detail_header = document.createElement("div");
    detail_header.classList.add("product-header");
    productDetail.appendChild(detail_header);
    let bundle2 = data.bundle !== "" ? data.bundle : data.title;
    let bundle = document.createElement("h2");
    bundle.classList.add("product-title");
    bundle.innerText = bundle2;
    detail_header.appendChild(bundle);

    if (data.props.length > 0) {
      let all_product_title = document.createElement("div");
      all_product_title.classList.add("all-bundle-titles");
      detail_header.appendChild(all_product_title);
      let p = document.createElement("p");
      p.classList.add("contains");
      p.innerText = "Contains";

      all_product_title.appendChild(p);

      data.props.forEach(function (prop) {
        let li = document.createElement("li");
        li.classList.add("prop-title");
        li.innerText = prop;
        all_product_title.appendChild(li);
      });
    }

    let removegrid = document.createElement("div");
    removegrid.classList.add("product-actions");
    detail_header.appendChild(removegrid);

    if (data.handles.length > 0) {
      let handles_url = document.createElement("div");
      handles_url.classList.add("handles_url");
      handles_url.classList.add("hidden");

      inner_wrapper.appendChild(handles_url);
      data.handles.forEach(function (handlee) {
        let handle_url = document.createElement("li");
        handle_url.innerText = handlee;
        handles_url.appendChild(handle_url);
      });
    }

    let removeProduct = document.createElement("div");
    removeProduct.classList.add("removed_saved_product");
    removegrid.appendChild(removeProduct);
    removeProduct.innerText = "Remove";

    let item_price = document.createElement("div");
    inner_wrapper.appendChild(item_price);

    let price = data.price;
    let formattedPrice = price.toLocaleString(undefined, {
      style: "currency",
      currency: Shopify.currency.active || "USD",
    });
    let item_pricee = `
  <div class="price-row">
    <div class="price-label">Item Price</div>
    <div class="price-value">$${price}</div>
  </div>
`;

    // Set innerHTML instead of appendChild
    item_price.innerHTML = item_pricee;

    let add_to_cart = document.createElement("div");
    add_to_cart.classList.add("button");
    add_to_cart.classList.add("saved_add_to_cart");
    add_to_cart.innerText = "Add to cart";
    item_price.appendChild(add_to_cart);
    // console.log(wrapper);
    return wrapper;
  }
}

customElements.define("saved-product", SavedLater);

let editBundle = document.querySelectorAll(".edit_button");

if (editBundle.length > 0) {
  editBundle.forEach(function (edit) {
    edit.addEventListener("click", function (event) {
      event.preventDefault();

      let itemKeyToRemove = edit.closest(".product-card").dataset.key;
      // console.log("itemKeyToRemove" , itemKeyToRemove)
      fetch("/cart/change.js", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: itemKeyToRemove,
          quantity: 0,
        }),
      })
        .then((res) => res.json())
        .then((cart) => {
          let edits = [];
          edit
            .closest(".product-card")
            .querySelectorAll(".cart__bundle_url li")
            .forEach(function (url) {
              edits.push(url.innerText.trim());
            });

          localStorage.setItem("_edit_bundle", JSON.stringify(edits));
          let Custom_Bucket = edit
            .closest(".product-card")
            .querySelector(".product-header")
            .getAttribute("bundle");
          localStorage.setItem(
            "_edit_bundle_name",
            JSON.stringify(Custom_Bucket)
          );
          window.location.href = `${edit.dataset.url}?edit_bundle`;
          // Optional: location.reload(); or close modal if needed
        });
    });
  });
}

// Save collection state before navigating to a product
document.addEventListener("click", function (e) {
  const productLink = e.target.closest('a[href*="/products/"]');
  if (!productLink) return;
  if (!window.location.pathname.includes("/collections/")) return;

  sessionStorage.setItem("collection_scroll", window.scrollY);
  sessionStorage.setItem("collection_url", window.location.href);

  const visibleProducts = document.querySelectorAll(".card-wrapper");
  sessionStorage.setItem("collection_product_count", visibleProducts.length);
});

// Restore collection state on back navigation
document.addEventListener("DOMContentLoaded", function () {
  if (!window.location.pathname.includes("/collections/")) return;

  const savedUrl = sessionStorage.getItem("collection_url");
  const savedScroll = sessionStorage.getItem("collection_scroll");
  const savedCount = parseInt(
    sessionStorage.getItem("collection_product_count")
  );

  if (
    !savedUrl ||
    savedUrl !== window.location.href ||
    !savedCount ||
    !savedScroll
  )
    return;

  function restoreProducts() {
    const currentCount = document.querySelectorAll(".card-wrapper").length;

    if (currentCount >= savedCount) {
      window.scrollTo(0, parseInt(savedScroll));
      sessionStorage.removeItem("collection_scroll");
      sessionStorage.removeItem("collection_url");
      sessionStorage.removeItem("collection_product_count");
      return;
    }

    const btn = document.getElementById("LoadMoreButton");
    if (btn && !btn.disabled) {
      btn.click();
      setTimeout(restoreProducts, 800);
    }
  }

  restoreProducts();
});
